import { api } from './index';

// Types
export type ContractType = 'SUPPLY_OF_ITEMS' | 'SUPPLY_AND_INSTALLATION' | 'WORKS';
export type ContractStatus = 'NOT_STARTED' | 'DRAFT' | 'ACTIVE' | 'EXPIRED' | 'TERMINATED';

export interface Contract {
  id: string;
  title: string;
  contractNumber: string;
  type: ContractType;
  supplierName: string;
  supplierId?: string;
  startDate: string | null;
  endDate: string | null;
  /** Original end date at creation; unchanged when the contract is extended */
  initialEndDate: string | null;
  contractValue: number;
  consumedValue: number;
  remainingBalance: number;
  currency: 'MVR' | 'USD';
  hasPriceCap: boolean;
  priceCap?: number;
  priceCapThreshold?: number;
  status: ContractStatus;
  templateId?: string;
  template?: ContractTemplate;
  createdById: string;
  createdBy?: {
    id: string;
    fullName: string;
    email: string;
  };
  documents?: ContractDocument[];
  alerts?: ContractAlert[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface ContractTemplate {
  id: string;
  name: string;
  type: ContractType;
  description?: string;
  fields: any; // JSON structure
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ContractDocument {
  id: string;
  contractId: string;
  documentType: string;
  version: number;
  fileName: string;
  bucket: string;
  objectKey: string;
  fileSize: number;
  mimeType: string;
  uploadedById?: string;
  uploadedBy?: {
    id: string;
    fullName: string;
    email: string;
  };
  uploadedAt: string;
  description?: string;
  isActive: boolean;
}

export interface ContractHistoryUser {
  id: string;
  fullName: string;
  email: string;
}

export interface ContractReferenceDocument {
  id: string;
  fileName: string;
  mimeType: string;
  documentType: string;
  version: number;
}

export interface ContractAuditLog {
  id: string;
  contractId: string;
  entityType: string;
  entityId: string;
  action: string;
  before?: any;
  after?: any;
  reason?: string;
  referenceDocumentId?: string;
  referenceDocument?: ContractReferenceDocument;
  changedById: string;
  changedBy?: ContractHistoryUser;
  createdAt: string;
}

export interface ContractExtension {
  id: string;
  contractId: string;
  initialEndDate: string | null;
  previousEndDate: string;
  newEndDate: string;
  reason: string;
  referenceDocumentId?: string;
  referenceDocument?: ContractReferenceDocument;
  changedById: string;
  changedBy?: ContractHistoryUser;
  createdAt: string;
}

export interface ContractRevision {
  id: string;
  contractId: string;
  previousContractValue: number;
  newContractValue: number;
  previousRemainingBalance: number;
  newRemainingBalance: number;
  reason: string;
  referenceDocumentId?: string;
  referenceDocument?: ContractReferenceDocument;
  changedById: string;
  changedBy?: ContractHistoryUser;
  createdAt: string;
}

export interface ContractAlert {
  id: string;
  contractId: string;
  alertType: 'EXPIRY' | 'PRICE_CAP';
  message: string;
  isRead: boolean;
  createdAt: string;
  acknowledgedAt?: string;
}

export interface ContractConsumption {
  id: string;
  contractId: string;
  contractDocumentId: string;
  poNumber: string;
  amount: number;
  consumedAt: string;
  uploadedById: string;
  uploadedBy?: {
    id: string;
    fullName: string;
    email: string;
  };
  contractDocument?: {
    id: string;
    fileName: string;
    mimeType: string;
  };
  createdAt: string;
}

export interface CreateContractDto {
  title: string;
  contractNumber?: string;
  type: ContractType;
  supplierName: string;
  supplierId?: string;
  startDate?: string | null;
  endDate?: string | null;
  contractValue: number;
  currency?: 'MVR' | 'USD';
  hasPriceCap: boolean;
  priceCap?: number;
  priceCapThreshold?: number;
  templateId?: string;
  status?: ContractStatus;
}

export interface UpdateContractDto {
  title?: string;
  contractNumber?: string;
  type?: ContractType;
  supplierName?: string;
  supplierId?: string;
  startDate?: string | null;
  endDate?: string | null;
  contractValue?: number;
  currency?: 'MVR' | 'USD';
  hasPriceCap?: boolean;
  priceCap?: number;
  priceCapThreshold?: number;
  templateId?: string;
  status?: ContractStatus;
}

export interface ContractFilterDto {
  status?: ContractStatus;
  type?: ContractType;
  expiringSoon?: boolean;
  search?: string;
}

export interface CreateContractTemplateDto {
  name: string;
  type: ContractType;
  description?: string;
  fields: any;
  isDefault?: boolean;
}

export interface UpdateContractTemplateDto {
  name?: string;
  type?: ContractType;
  description?: string;
  fields?: any;
  isDefault?: boolean;
}

export interface UploadContractDocumentDto {
  documentType: string;
  description?: string;
  version?: number;
}

export interface UpdateContractDocumentDto {
  documentType?: string;
  description?: string | null;
}

export interface ReplaceContractDocumentDto {
  documentType?: string;
  description?: string | null;
}

export interface UpdateContractConsumptionDto {
  poNumber?: string;
  amount?: number;
  contractDocumentId?: string;
}

export interface CreateContractExtensionDto {
  extensionDays: number;
  reason: string;
  referenceDocumentId: string;
}

export interface CreateContractRevisionDto {
  newContractValue: number;
  reason: string;
  referenceDocumentId: string;
}

export interface DashboardStats {
  totalContracts: number;
  activeContracts: number;
  expiredContracts: number;
  upcomingContracts: number;
  totalAgreementValue: number;
  totalConsumedValue: number;
  totalRemainingBalance: number;
}

export interface ContractImportRowResult {
  contractNumber: string;
  outcome: 'created' | 'skipped' | 'error';
  message?: string;
}

export interface ContractImportResult {
  total: number;
  created: number;
  skipped: number;
  errors: number;
  rows: ContractImportRowResult[];
}

// API Client
export const contractsApi = {
  getNextContractNumber: async (): Promise<{ contractNumber: string }> => {
    const response = await api.get('/contracts/next-number');
    return response.data;
  },

  create: async (data: CreateContractDto): Promise<Contract> => {
    const response = await api.post('/contracts', data);
    return response.data;
  },

  bulkImport: async (file: File, dryRun = false): Promise<ContractImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dryRun', dryRun ? 'true' : 'false');

    const response = await api.post('/contracts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  list: async (filters?: ContractFilterDto): Promise<Contract[]> => {
    const response = await api.get('/contracts', { params: filters });
    return response.data;
  },

  get: async (id: string): Promise<Contract> => {
    const response = await api.get(`/contracts/${id}`);
    return response.data;
  },

  update: async (id: string, data: UpdateContractDto): Promise<Contract> => {
    const response = await api.put(`/contracts/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/contracts/${id}`);
  },

  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/contracts/dashboard/stats');
    return response.data;
  },

  acknowledgeAlert: async (contractId: string, alertId: string): Promise<void> => {
    await api.post(`/contracts/${contractId}/alerts/${alertId}/acknowledge`);
  },

  updateConsumedValue: async (
    id: string,
    payload: { amount: number; poNumber: string; contractDocumentId: string }
  ): Promise<Contract> => {
    const response = await api.patch(`/contracts/${id}/consumed-value`, payload);
    return response.data;
  },

  listConsumptions: async (id: string): Promise<ContractConsumption[]> => {
    const response = await api.get(`/contracts/${id}/consumptions`);
    return response.data;
  },

  updateConsumption: async (
    id: string,
    consumptionId: string,
    payload: UpdateContractConsumptionDto
  ): Promise<{ contract: Contract; consumption: ContractConsumption }> => {
    const response = await api.patch(`/contracts/${id}/consumptions/${consumptionId}`, payload);
    return response.data;
  },

  createExtension: async (
    id: string,
    payload: CreateContractExtensionDto
  ): Promise<ContractExtension> => {
    const response = await api.post(`/contracts/${id}/extensions`, payload);
    return response.data;
  },

  listExtensions: async (id: string): Promise<ContractExtension[]> => {
    const response = await api.get(`/contracts/${id}/extensions`);
    return response.data;
  },

  createRevision: async (
    id: string,
    payload: CreateContractRevisionDto
  ): Promise<ContractRevision> => {
    const response = await api.post(`/contracts/${id}/revisions`, payload);
    return response.data;
  },

  listRevisions: async (id: string): Promise<ContractRevision[]> => {
    const response = await api.get(`/contracts/${id}/revisions`);
    return response.data;
  },

  listAuditLogs: async (id: string): Promise<ContractAuditLog[]> => {
    const response = await api.get(`/contracts/${id}/audit-logs`);
    return response.data;
  },
};

export const contractTemplatesApi = {
  create: async (data: CreateContractTemplateDto): Promise<ContractTemplate> => {
    const response = await api.post('/contracts/templates', data);
    return response.data;
  },

  list: async (type?: ContractType): Promise<ContractTemplate[]> => {
    const response = await api.get('/contracts/templates', { params: { type } });
    return response.data;
  },

  get: async (id: string): Promise<ContractTemplate> => {
    const response = await api.get(`/contracts/templates/${id}`);
    return response.data;
  },

  update: async (id: string, data: UpdateContractTemplateDto): Promise<ContractTemplate> => {
    const response = await api.put(`/contracts/templates/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/contracts/templates/${id}`);
  },

  getDefault: async (type: ContractType): Promise<ContractTemplate> => {
    const response = await api.get(`/contracts/templates/default/${type}`);
    return response.data;
  },
};

export const contractDocumentsApi = {
  upload: async (
    contractId: string,
    file: File,
    data: UploadContractDocumentDto
  ): Promise<ContractDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', data.documentType);
    if (data.description) formData.append('description', data.description);
    if (data.version) formData.append('version', data.version.toString());

    const response = await api.post(`/contracts/${contractId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  list: async (contractId: string): Promise<ContractDocument[]> => {
    const response = await api.get(`/contracts/${contractId}/documents`);
    return response.data;
  },

  update: async (
    contractId: string,
    documentId: string,
    data: UpdateContractDocumentDto
  ): Promise<ContractDocument> => {
    const response = await api.patch(`/contracts/${contractId}/documents/${documentId}`, data);
    return response.data;
  },

  replace: async (
    contractId: string,
    documentId: string,
    file: File,
    data: ReplaceContractDocumentDto
  ): Promise<ContractDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    if (data.documentType) formData.append('documentType', data.documentType);
    if (data.description !== undefined && data.description !== null) {
      formData.append('description', data.description);
    }

    const response = await api.post(`/contracts/${contractId}/documents/${documentId}/replace`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getUrl: async (id: string): Promise<{ url: string; fileName: string; mimeType: string }> => {
    const response = await api.get(`/contracts/documents/${id}/url`);
    return response.data;
  },

  download: async (id: string): Promise<Blob> => {
    const response = await api.get(`/contracts/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/contracts/documents/${id}`);
  },
};

export const contractAlertsApi = {
  acknowledge: async (contractId: string, alertId: string): Promise<void> => {
    await contractsApi.acknowledgeAlert(contractId, alertId);
  },
};

